/**
 * Simulation Engine
 *
 * Overlays simulated visits onto capacity snapshots WITHOUT persisting data.
 * All simulations are in-memory only.
 */

import { ProviderCapacityDay } from '../types/capacity.types';
import { SimulatedVisitBlock } from '../types/simulation.types';
import {
  calculateUtilization,
  calculateRemainingMinutes,
  isOverCapacity,
  assessRiskLevel,
} from '../engine/utilizationCalculator';

/**
 * Overlay simulated visit blocks onto a capacity snapshot
 *
 * IMPORTANT: This function does NOT persist data. It creates a new snapshot
 * with projected utilization calculated from simulated visits.
 *
 * @param baselineSnapshot - Original capacity snapshot
 * @param visitBlocks - Simulated visit blocks to overlay
 * @param providerId - Provider to assign visits to
 * @returns New snapshot with projectedUtilization calculated
 */
export function overlaySimulation(
  baselineSnapshot: ProviderCapacityDay[],
  visitBlocks: SimulatedVisitBlock[],
  providerId: number
): ProviderCapacityDay[] {
  // Clone the snapshot to avoid mutation
  const simulatedSnapshot = baselineSnapshot.map((day) => ({ ...day }));

  // Group visit blocks by date for quick lookup
  const blocksByDate = new Map<string, number>();
  visitBlocks.forEach((block) => {
    const existing = blocksByDate.get(block.date) || 0;
    blocksByDate.set(block.date, existing + block.durationMinutes);
  });

  // Overlay simulated visits and recalculate metrics
  simulatedSnapshot.forEach((day) => {
    // Only update days for the assigned provider
    if (day.providerId !== providerId) {
      return;
    }

    // Check if there are simulated visits for this day
    const simulatedMinutes = blocksByDate.get(day.date) || 0;
    if (simulatedMinutes === 0) {
      return;
    }

    // Calculate new visit minutes
    const newVisitMinutes = day.visitMinutes + simulatedMinutes;

    // Recalculate utilization with simulated visits
    const projectedUtilization = calculateUtilization(
      day.shiftMinutes,
      newVisitMinutes,
      day.breakMinutes
    );

    // Recalculate remaining minutes
    const newRemainingMinutes = calculateRemainingMinutes(
      day.shiftMinutes,
      newVisitMinutes,
      day.breakMinutes
    );

    // Recalculate over capacity status
    const newOverCapacity = isOverCapacity(
      newVisitMinutes,
      day.shiftMinutes,
      day.breakMinutes
    );

    // Recalculate risk level
    const newRiskLevel = assessRiskLevel(projectedUtilization, newRemainingMinutes);

    // Update the day with projected values
    day.projectedUtilization = projectedUtilization;
    day.remainingMinutes = newRemainingMinutes;
    day.isOverCapacity = newOverCapacity;
    day.riskLevel = newRiskLevel;
  });

  return simulatedSnapshot;
}

/**
 * Clear simulation from a snapshot (remove projected values)
 */
export function clearSimulation(
  snapshot: ProviderCapacityDay[]
): ProviderCapacityDay[] {
  return snapshot.map((day) => {
    // Remove projected utilization
    const { projectedUtilization, ...rest } = day;
    return rest;
  });
}

/**
 * Check if a snapshot has any simulated data
 */
export function hasSimulation(snapshot: ProviderCapacityDay[]): boolean {
  return snapshot.some((day) => day.projectedUtilization !== undefined);
}
